import { useEffect, useId, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import './Modal.styl';

type Props = {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  width?: number;
  eyebrow?: ReactNode;
  meta?: ReactNode;
  children: ReactNode;
};

export function Modal({ open, title, onClose, width = 520, eyebrow, meta, children }: Props) {
  const titleId = useId();
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="o-modal" onClick={onClose}>
      <div
        className="o-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ '--modal-w': `${width}px` } as CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="o-modal__head">
          <div>
            {eyebrow ? <div className="o-modal__eyebrow">{eyebrow}</div> : null}
            <h2 className="o-modal__title" id={titleId}>
              {title}
            </h2>
            {meta ? <div className="o-modal__meta">{meta}</div> : null}
          </div>
          <button type="button" className="o-modal__close" onClick={onClose} aria-label={t('common.close')}>
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}
